import * as React from "react";
import {
    Select,
    SelectProps,
    Button,
    SpaceBetween,
    Container,
    Link,
    Alert,
    Spinner,
    Box,
    NonCancelableCustomEvent, Header
} from "@amzn/awsui-components-react";
import { scroller } from "react-scroll";
import CreateSubscriptionResult from "../../../update_pharmacy_customer/components/subscription/components/create_subscription/CreateSubscriptionResult";
import { API } from "aws-amplify";

export interface State {
    customerType: string;
    errorMessage: string;
    formSubmitted: boolean;
    response: any;
    resultSuccessful: boolean;
    showLoadingSpinner: boolean;
}

export interface ProviderProps {
    customerTypePaths: { [key: string]: string };
    customerTypeMapping: { [key: string]: string };
    title?: string;
    dashboardLink?: string;
}

export class BaseCachedPharmacyCustomerProvider extends React.Component<ProviderProps, State> {
    constructor(props: ProviderProps) {
        super(props);

        this.state = {
            customerType: "Default",
            errorMessage: "",
            formSubmitted: false,
            response: null,
            resultSuccessful: false,
            showLoadingSpinner: false,
        };
    }

    protected handleCustomerTypeChange = (
        event: NonCancelableCustomEvent<SelectProps.ChangeDetail>
    ): void => {
        if (event.detail.selectedOption?.value) {
            this.setState({
                customerType: event.detail.selectedOption.value
            });
        }
    };

    protected handleSubmit = (): void => {
        this.setState({ formSubmitted: true, resultSuccessful: false, errorMessage: "" });

        const apiName = "ApaqPortalAPI";
        let path = "/cached-pharmacy-customer/get?";

        const customerType = this.state.customerType;
        if (this.props.customerTypePaths[customerType]) {
            path += this.props.customerTypePaths[customerType];
        } else {
            this.setState({ errorMessage: "No such customer type", formSubmitted: false });
            return;
        }

        API.get(apiName, path, {})
            .then((response: any) => {
                this.setState({
                    response,
                    formSubmitted: false,
                    resultSuccessful: true
                }, () => {
                    this.scrollToResults();
                });
            })
            .catch((error: any) => {
                this.setState({
                    errorMessage: error.response?.data?.message || "An error occurred",
                    formSubmitted: false
                });
            });
    };

    protected scrollToResults(): void {
        scroller.scrollTo("results-section", {
            duration: 800,
            delay: 0,
            smooth: "easeInOutQuart",
            offset: -50,
        });
    }

    render() {
        const selectOptions = Object.entries(this.props.customerTypeMapping).map(([value, label], index) => ({
            value,
            label: `${index + 1}: ${label}`
        }));

        return (
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                {/* Title Container */}
                <Container>
                    <Box padding={{ top: 'l', bottom: 'm' }}>
                        <Header
                            variant="h1"
                            description={
                                <Link
                                    href={this.props.dashboardLink || "https://w.amazon.com/bin/view/Pharmacy/Public/Teams/PharmacySDET/CPCPCustomerDashboard-ApaqPortalUIBeta/"}
                                    external
                                >
                                    CPCP Customer Inventory Dashboard
                                </Link>
                            }
                        >
                            {this.props.title}
                        </Header>
                    </Box>
                </Container>

                {/* Main Content Container */}
                <Container>
                    <SpaceBetween size="l">
                        <div style={{
                            position: 'sticky',
                            top: 0,
                            backgroundColor: 'white',
                            zIndex: 100,
                            padding: '16px',
                            borderBottom: '1px solid #e9ebed'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <div style={{ width: '500px' }}>
                                    <Select
                                        selectedOption={{
                                            value: this.state.customerType,
                                            label: this.props.customerTypeMapping[this.state.customerType]
                                        }}
                                        onChange={this.handleCustomerTypeChange}
                                        options={selectOptions}
                                        placeholder="Choose a customer type"
                                        selectedAriaLabel="Selected"
                                    />
                                </div>
                                <Button
                                    variant="primary"
                                    onClick={this.handleSubmit}
                                    disabled={!this.state.customerType}
                                >
                                    Submit
                                </Button>
                            </div>
                        </div>

                        <div style={{ marginTop: '16px' }}>
                            {this.renderResults()}
                        </div>
                    </SpaceBetween>
                </Container>
            </div>
        );
    }

    protected renderResults() {
        return (
            <>
                {this.state.formSubmitted && (
                    <Box textAlign="center">
                        <Spinner size="large" />
                    </Box>
                )}

                {this.state.errorMessage && (
                    <Alert type="error" header="Error">
                        {this.state.errorMessage}
                    </Alert>
                )}

                {this.state.resultSuccessful && (
                    <CreateSubscriptionResult
                        resultSuccessful={this.state.resultSuccessful}
                        response={this.state.response}
                    />
                )}
            </>
        );
    }
}
